import { useState } from 'react';
import { ReactComponent as CloseIcon } from '@assets/icons/close.svg';
import config from 'config';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import Image from 'next/image';

import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useBoolean,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeAddons,
  removeProvider,
  setAddons
} from 'store/reducers/bookingAppointmentSlice';

import { convertDuration, formatCurrency } from 'utils';

import { ExtendedAddonInterface } from 'types';

import { Loader } from 'ui';

const {
  booking: { defaultProvider, DefaultProviderAvatar }
} = config;

interface AppointmentItemProps {
  addon: ExtendedAddonInterface;
}

const AppointmentDetailsItem = ({ addon }: AppointmentItemProps) => {
  const dispatch = useAppDispatch();
  const showSvcPrice = useShowSvcPrice();
  const { selectedAddons, selectedProviders } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const {
    colors: {
      palette: {
        booking: {
          stepTime: {
            apptDetailsList: {
              detailsItem: { addonItem }
            }
          }
        }
      }
    }
  } = useTheme();

  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);

  const iconSize = useBreakpointValue({ base: 18, md: 24 }, { fallback: 'md' });
  const provider = selectedProviders[addon.id];
  const isDefaultProvider = provider?.id === defaultProvider.id;
  const providerName = isDefaultProvider
    ? defaultProvider.name
    : provider?.name;
  const providerDescription = isDefaultProvider
    ? defaultProvider.description
    : provider?.description;

  const updateAddons = () => {
    const updatedAddons = selectedAddons[addon.parentServiceId].filter(
      (item) => item.id !== addon.id
    );
    if (updatedAddons.length === 0) {
      dispatch(removeAddons({ service: addon.parentServiceId }));
    } else {
      dispatch(
        setAddons({ service: addon.parentServiceId, addons: updatedAddons })
      );
    }
    dispatch(removeProvider({ service: addon.id }));
  };

  return (
    <Flex
      align='center'
      bg={addonItem.background}
      borderRadius='6px'
      py='20px'
      pl={{ base: 0, md: '5px' }}
      pr={{ base: '17px', md: '20px', lg: '30px', xl: '40px' }}
      alignItems='center'
      gap={{ base: '0', md: '7px' }}
    >
      <Box>
        <Button
          w='40px'
          h='40px'
          mx='10px'
          p={0}
          variant='text'
          onClick={updateAddons}
        >
          <CloseIcon width={iconSize} height={iconSize} />
        </Button>
      </Box>
      <Flex
        gap='10px'
        alignItems='center'
        justifyContent='space-between'
        w='100%'
      >
        <Stack gap='14px'>
          <Text
            color={addonItem.addonNameTextColor}
            fontSize={{ base: '20px', md: '23px', xl: '25px' }}
          >
            {addon?.name}
          </Text>

          <Flex alignItems='center' gap='10px'>
            <Box
              w='40px'
              h='40px'
              borderRadius='50%'
              overflow='hidden'
              position='relative'
              flexShrink={0}
            >
              {provider?.public_photo_url?.includes('http') && !imageError ? (
                <>
                  {loading && <Loader absolute />}
                  <Image
                    src={provider?.public_photo_url}
                    alt={provider.name}
                    sizes='(max-width: 450px) 40px, 65px'
                    width={40}
                    height={40}
                    onError={() => setImageError(true)}
                    onLoad={setLoading.off}
                  />
                </>
              ) : (
                <DefaultProviderAvatar
                  width={40}
                  height={40}
                  fill={addonItem.providerIconColor}
                  style={{ opacity: 0.5 }}
                />
              )}
            </Box>
            <Stack gap='2px' justifyContent='center'>
              <Text
                color={addonItem.providerNameTextColor}
                fontSize='12px'
                textTransform='uppercase'
              >
                {providerName}
              </Text>

              {providerDescription && (
                <Text
                  color={addonItem.providerDescriptionTextColor}
                  fontSize='12px'
                  mt='5px'
                >
                  {providerDescription}
                </Text>
              )}
            </Stack>
          </Flex>
        </Stack>
      </Flex>

      <Stack alignItems='flex-end' gap='6px' ml='20px' minW='90px'>
        {showSvcPrice && (
          <Text
            color={addonItem.addonPriceTextColor}
            fontSize='25px'
            lineHeight='125%'
          >
            {formatCurrency(addon.retail)}
          </Text>
        )}
        <Text
          color={addonItem.addonDurationTextColor}
          fontSize='15px'
          whiteSpace='no-wrap'
        >
          {convertDuration(`${addon.duration}`) || 'No specified'}
        </Text>
      </Stack>
    </Flex>
  );
};

export default AppointmentDetailsItem;
